import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
} from '@angular/core';
import { AsyncValidatorFn, FormControl, ValidatorFn } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import {
  DataSourceParams,
  Loading,
  PageableDataSource,
  SelectOption,
  TemplateDirective,
  ThemeMode,
  ThemeModeService,
} from '@fleet-beat/core';
import { InfiniteListScrollDirective } from '@fleet-beat/infinite-list';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { EditorFieldType, EditorFieldTypeEnumService } from '../../enums/editor-field-type-enum.service';
import { EditorFieldConfigCombined } from '../../models/editor-field-config.model';
import { EditorHelperService } from '../../services/editor-helper.service';

/* eslint-disable @typescript-eslint/member-ordering */
@Component({
  selector: 'fb-editor-field',
  templateUrl: './editor-field.component.html',
  styleUrls: ['./editor-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditorFieldComponent extends OnDestroyMixin implements OnInit {
  @Input() set config(value: EditorFieldConfigCombined) {
    const config: EditorFieldConfigCombined = {
      ...value,
      required: this.isFieldRequired === undefined ? value.required : this.isFieldRequired,
    };
    this.configValueSubject.next(config);
  }
  get config(): EditorFieldConfigCombined {
    return this.configValueSubject.getValue();
  }

  @Input() set control(control: FormControl) {
    this.controlSubject.next(control);
  }
  get control(): FormControl {
    return this.controlSubject.getValue();
  }

  @Input() set required(value: boolean) {
    this.isFieldRequired = value;
    this.config = { ...this.config, required: value };
  }
  get required(): boolean {
    return this.config?.required || false;
  }

  @Input() set min(value: number | string) {
    this.config = { ...this.config, min: value };
  }
  get min(): number | string {
    return this.config?.min;
  }

  @Input() set max(value: number | string) {
    this.config = { ...this.config, max: value };
  }
  get max(): number | string {
    return this.config?.max;
  }

  @Input() set minLength(value: number) {
    this.config = { ...this.config, minLength: value };
  }
  get minLength(): number {
    return this.config?.minLength;
  }

  @Input() set maxLength(value: number) {
    this.config = { ...this.config, maxLength: value };
  }
  get maxLength(): number {
    return this.config?.maxLength;
  }

  @Input() set label(value: string) {
    this.config = { ...this.config, label: value };
  }
  get label(): string {
    return this.config?.label;
  }

  @Input() set placeholder(value: string) {
    this.config = { ...this.config, placeholder: value };
  }
  get placeholder(): string {
    return this.config?.placeholder;
  }

  @Input() set options(value: SelectOption[]) {
    this.config = { ...this.config, options: value };
  }
  get options(): SelectOption[] {
    return this.config?.options;
  }

  @Input() type: EditorFieldType;
  @Input() disabled: boolean;
  @Input() dataStorage: PageableDataSource<Observable<SelectOption[]>, DataSourceParams<object, object>>;
  @Input() disabledItems: object[];
  @Input() readonly: boolean = false;
  @Input() infiniteListScroll: InfiniteListScrollDirective;
  @Input() loading: Loading = new Loading();
  @Input() templates: QueryList<TemplateDirective<object>>;
  @Output() focusEmit: EventEmitter<boolean> = new EventEmitter(undefined);
  @Output() blurEmit: EventEmitter<boolean> = new EventEmitter(undefined);

  currentThemeSubject: BehaviorSubject<ThemeMode>;

  readonly editorFieldTypeEnum: Record<string, EditorFieldType> = this.editorFieldTypeEnumService.getAll();
  readonly defaultAppearance: MatFormFieldAppearance = 'outline';

  private controlSubject: BehaviorSubject<FormControl> = new BehaviorSubject(undefined);
  private configValueSubject: BehaviorSubject<EditorFieldConfigCombined> = new BehaviorSubject(undefined);
  private isFieldRequired: boolean;

  constructor(
    private editorFieldTypeEnumService: EditorFieldTypeEnumService,
    private editorHelperService: EditorHelperService,
    private changeDetectorRef: ChangeDetectorRef,
    private themeModeService: ThemeModeService,
  ) {
    super();
    this.currentThemeSubject = this.themeModeService.themeModeSubject;
  }

  ngOnInit(): void {
    this.initListeners();
  }

  trimStringValue(): void {
    const value = this.control?.value;
    if (typeof value !== 'string') {
      return;
    }
    const trimmedValue: string = value.trim();
    if (trimmedValue !== value) {
      this.control.setValue(trimmedValue);
    }
  }

  clear(): void {
    this.control.setValue(undefined);
  }

  private initListeners(): void {
    this.addValidations();

    this.controlSubject
      .pipe(
        filter(Boolean),
        switchMap((control: FormControl) => control.statusChanges),
        untilComponentDestroyed(this),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
  }

  private addValidations(): void {
    combineLatest([this.configValueSubject, this.controlSubject])
      .pipe(
        filter(([config, control]: [EditorFieldConfigCombined, FormControl]) => Boolean(config) && Boolean(control)),
        map(([config, control]: [EditorFieldConfigCombined, FormControl]) => {
          const asyncValidators: AsyncValidatorFn[] = config.asyncValidators;
          const field: EditorFieldConfigCombined = {
            ...(config || {}),
            type: this.type || config.type,
          };
          return { control, validators: this.editorHelperService.getValidators(field), asyncValidators };
        }),
        untilComponentDestroyed(this),
      )
      .subscribe((data: { control: FormControl; validators: ValidatorFn[]; asyncValidators: AsyncValidatorFn[] }) => {
        data.control.setValidators(data.validators);
        if (data.asyncValidators) {
          data.control.setAsyncValidators(data.asyncValidators);
        }

        data.control.updateValueAndValidity({ emitEvent: false });
        this.changeDetectorRef.markForCheck();
      });
  }
}
